import type { TranslationKeys } from '@vocab/core';
import { observer } from 'mobx-react';
import { useTranslations } from '@vocab/react';
import { Stack, Text, TextLinkButton } from 'braid-design-system';

import {
  RESUME,
  COVER_LETTER,
  SELECTION_CRITERIA,
} from 'src/constants/attachmentTypes';
import type { IAttachment } from 'src/stores/JobApplicationStore/Attachment';
import { trackDownloadDocumentEvent } from 'src/utils/events';
import translations from './.vocab';
import { TRACKING_CANDIDATE_DETAILS } from 'src/constants/pageTrackingData';
import downloadFile from 'src/DocumentViewer/utils/downloadFile';
import type { TrackStatusUpdateAttributes } from 'src/utils/eventsTypes';

interface Props {
  attachments: IAttachment[];
  candidateId: string;
  isVerifiedId: TrackStatusUpdateAttributes['isVerifiedId'];
}

export type AttachmentLabelNameTranslationKeys = TranslationKeys<
  typeof translations
>;

const getLabelFromAttachmentType = (
  type: number,
): AttachmentLabelNameTranslationKeys => {
  switch (type) {
    case RESUME.type:
      return 'Resumé';
    case COVER_LETTER.type:
      return 'Cover Letter';
    case SELECTION_CRITERIA.type:
      return 'Selection Criteria';
    default:
      return 'Cover Letter';
  }
};

const AttachmentList = ({ attachments, candidateId, isVerifiedId }: Props) => {
  const { t } = useTranslations(translations);
  const attachmentItems = attachments.map((attachment) => {
    const attachmentLabel = attachment.filename
      ? attachment.filename
      : t(getLabelFromAttachmentType(attachment.type));

    const handleClickAttachment = () => {
      downloadFile(attachment.downloadUrl, () => {
        trackDownloadDocumentEvent({
          candidateId,
          prospectId: attachment.jobApplication.id,
          documentType: attachment.typeName,
          attachmentId: attachment.id,
          currentPage: TRACKING_CANDIDATE_DETAILS,
          isVerifiedId,
        });
      });
    };
    return (
      <Text baseline={false} key={attachment.id}>
        <TextLinkButton onClick={handleClickAttachment}>
          {attachmentLabel}
        </TextLinkButton>
      </Text>
    );
  });
  return <Stack space="large">{attachmentItems}</Stack>;
};

export default observer(AttachmentList);
